import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useCancelImportSession,
  useCommitImportSession,
  useCreateImportSession,
  useImportProfiles,
} from "../hooks/import";
import type { CommitResult, CreateSessionResponse } from "../lib/import";

type Step = "upload" | "preview" | "result";

export function ImportPage() {
  const { t } = useTranslation();
  const profilesQuery = useImportProfiles();
  const createMut = useCreateImportSession();
  const commitMut = useCommitImportSession();
  const cancelMut = useCancelImportSession();

  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [profileCode, setProfileCode] = useState<string>("");
  const [session, setSession] = useState<CreateSessionResponse | null>(null);
  const [result, setResult] = useState<CommitResult | null>(null);

  async function handleUpload() {
    if (!file || !profileCode) return;
    const resp = await createMut.mutateAsync({ file, profileCode });
    setSession(resp);
    setStep("preview");
  }

  async function handleCommit() {
    if (!session) return;
    const r = await commitMut.mutateAsync(session.sessionId);
    setResult(r);
    setStep("result");
  }

  async function handleCancel() {
    if (session) await cancelMut.mutateAsync(session.sessionId);
    setSession(null);
    setFile(null);
    setProfileCode("");
    setStep("upload");
  }

  return (
    <section className="space-y-5">
      <div>
        <div
          className="text-xs font-semibold uppercase tracking-wider mb-1"
          style={{ color: "var(--muted)" }}
        >
          {t("nav.group_reports")}
        </div>
        <h1 className="text-2xl font-bold tracking-tight">{t("import.title")}</h1>
      </div>

      {step === "upload" && (
        <section className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium">{t("import.step1.select_profile")}</span>
            <select
              value={profileCode}
              onChange={(e) => setProfileCode(e.target.value)}
              className="border rounded px-2 py-1 w-full mt-1"
            >
              <option value="">—</option>
              {profilesQuery.data?.profiles.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium">{t("import.step1.select_file")}</span>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-1 block"
            />
          </label>
          <button
            type="button"
            disabled={!file || !profileCode || createMut.isPending}
            onClick={() => void handleUpload()}
            className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
          >
            {t("import.step1.submit")}
          </button>
          {createMut.error && <p className="text-red-600 text-sm">{createMut.error.message}</p>}
        </section>
      )}

      {step === "preview" && session !== null && (
        <section className="space-y-4">
          <div className="border rounded p-3 bg-slate-50">
            <p>
              {t("import.step2.summary", {
                validRows: session.summary.validRows,
                totalRows: session.summary.totalRows,
                skipped: session.summary.skippedRows,
              })}
            </p>
          </div>

          <details open>
            <summary className="cursor-pointer text-blue-600">
              {t("import.step2.will_create")} ({session.preview.willCreate.length})
            </summary>
            <ul className="ml-4 mt-2 list-disc text-sm">
              {session.preview.willCreate.map((p) => (
                <li key={`${p.kind}:${p.label}`}>{p.label}</li>
              ))}
            </ul>
          </details>

          {session.preview.willSkip.length > 0 && (
            <details>
              <summary className="cursor-pointer text-slate-600">
                {t("import.step2.will_skip")} ({session.preview.willSkip.length})
              </summary>
              <ul className="ml-4 mt-2 list-disc text-sm">
                {session.preview.willSkip.map((p) => (
                  <li key={`${p.kind}:${p.label}`}>{p.label}</li>
                ))}
              </ul>
            </details>
          )}

          {session.preview.conflicts.length > 0 && (
            <details open>
              <summary className="cursor-pointer text-amber-700">
                {t("import.step2.conflicts")} ({session.preview.conflicts.length})
              </summary>
              <ul className="ml-4 mt-2 list-disc text-sm">
                {session.preview.conflicts.map((p) => (
                  <li key={`${p.kind}:${p.label}`}>
                    {p.label}
                    {p.details !== undefined ? ` — ${p.details}` : ""}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {session.warnings.length > 0 && (
            <details>
              <summary className="cursor-pointer text-amber-700">
                {t("import.step2.warnings")} ({session.warnings.length}
                {session.hasMoreWarnings ? "+" : ""})
              </summary>
              <ul className="ml-4 mt-2 list-disc text-sm">
                {session.warnings.map((w) => (
                  <li key={`${w.rowIndex}:${w.reason}`}>{w.reason}</li>
                ))}
              </ul>
            </details>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void handleCancel()}
              className="px-4 py-2 border rounded"
            >
              {t("import.step2.cancel_button")}
            </button>
            <button
              type="button"
              onClick={() => void handleCommit()}
              disabled={commitMut.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
            >
              {t("import.step2.commit_button")}
            </button>
          </div>

          {commitMut.error && <p className="text-red-600 text-sm">{commitMut.error.message}</p>}
        </section>
      )}

      {step === "result" && result !== null && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">{t("import.step3.success_title")}</h2>
          <p>
            {t("import.step3.result_summary", {
              created: result.createdCount,
              skipped: result.skippedCount,
            })}
          </p>
          {result.errors.length > 0 && (
            <details>
              <summary className="text-red-600">
                {t("import.step3.errors_title")} ({result.errors.length})
              </summary>
              <ul className="ml-4 list-disc text-sm">
                {result.errors.map((e) => (
                  <li key={`${e.rowIndex}:${e.reason}`}>
                    Zeile {e.rowIndex}: {e.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div className="flex gap-2">
            <a href="/investments" className="px-4 py-2 border rounded">
              {t("import.step3.to_investments")}
            </a>
            <button
              type="button"
              onClick={() => void handleCancel()}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              {t("import.step3.new_import")}
            </button>
          </div>
        </section>
      )}
    </section>
  );
}
